import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Shield, Lock, CreditCard, ExternalLink, CheckCircle, XCircle } from 'lucide-react';

const EmbeddedCheckoutModal = ({ 
  isOpen, 
  onClose, 
  planId, 
  planName,
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, processing, success, cancelled
  const popupRef = useRef(null);
  const checkIntervalRef = useRef(null);

  // Create checkout session when modal opens
  useEffect(() => {
    if (!isOpen || !planId) return;

    const initCheckout = async () => {
      setLoading(true);
      setError(null);
      setCheckoutUrl(null);
      setPaymentStatus('idle');

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/subscription`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              plan_id: planId,
              origin_url: window.location.origin
            })
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to create checkout session');
        }

        const data = await response.json();
        
        if (data.checkout_url) {
          setCheckoutUrl(data.checkout_url);
        } else {
          throw new Error('No checkout URL received');
        }
      } catch (err) {
        console.error('Checkout initialization error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initCheckout();

    // Cleanup on unmount
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isOpen, planId]);

  const openCheckoutPopup = () => {
    if (!checkoutUrl) return;

    setPaymentStatus('processing');

    // Calculate popup dimensions and position
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Open Stripe checkout in popup
    popupRef.current = window.open(
      checkoutUrl,
      'StripeCheckout',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    // Monitor popup for closure
    checkIntervalRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        clearInterval(checkIntervalRef.current);
        // User closed the popup - they either completed payment or cancelled
        // We'll check if payment was successful
        handlePopupClosed();
      }
    }, 500);
  };

  const handlePopupClosed = async () => {
    // Check if subscription was updated via actual payment (not just trial)
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/subscription`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Only show success if subscription is active AND it's a paid plan (not free/trial)
        const isPaidPlan = data.subscription?.plan_id && 
                          data.subscription.plan_id !== 'free' && 
                          data.subscription.status === 'active' &&
                          !data.subscription?.is_trial;
        
        if (isPaidPlan) {
          setPaymentStatus('success');
          setTimeout(() => {
            if (onSuccess) onSuccess();
            onClose();
          }, 2000);
          return;
        }
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
    
    // If payment wasn't completed, show cancelled state
    setPaymentStatus('cancelled');
  };

  const handleClose = () => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0">
        <DialogHeader className="p-4 pb-3 border-b bg-gradient-to-r from-teal-50 to-cyan-50">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Lock className="h-5 w-5 text-green-600" />
            Secure Checkout
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-1">{planName}</p>
        </DialogHeader>

        <div className="p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600 mb-3" />
              <p className="text-gray-600">Preparing secure checkout...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-6">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={handleClose} variant="outline">
                Close and try again
              </Button>
            </div>
          )}

          {paymentStatus === 'success' && (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-green-700 mb-2">Payment Successful!</h3>
              <p className="text-gray-600">Your subscription is now active.</p>
            </div>
          )}

          {paymentStatus === 'cancelled' && (
            <div className="flex flex-col items-center justify-center py-8">
              <XCircle className="h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">Payment Not Completed</h3>
              <p className="text-gray-600 mb-4">The checkout window was closed before payment was completed.</p>
              <Button 
                onClick={() => setPaymentStatus('idle')} 
                className="bg-teal-600 hover:bg-teal-700"
              >
                Try Again
              </Button>
            </div>
          )}

          {checkoutUrl && paymentStatus !== 'success' && (
            <div className="space-y-6">
              {paymentStatus === 'processing' ? (
                <div className="text-center py-4">
                  <Loader2 className="h-8 w-8 animate-spin text-teal-600 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">Complete payment in the popup window</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Don&apos;t see the window? Check if it was blocked by your browser.
                  </p>
                  <Button 
                    onClick={openCheckoutPopup} 
                    variant="link" 
                    className="mt-2 text-teal-600"
                  >
                    Click here to reopen
                  </Button>
                </div>
              ) : (
                <>
                  <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <CreditCard className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Secure Payment</h4>
                        <p className="text-xs text-gray-500">Powered by Stripe</p>
                      </div>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-1.5">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        All major credit cards accepted
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Apple Pay & Google Pay supported
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Cancel anytime, no lock-in
                      </li>
                    </ul>
                  </div>

                  <Button 
                    onClick={openCheckoutPopup}
                    className="w-full h-12 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-bold"
                  >
                    <CreditCard className="h-5 w-5 mr-2" />
                    Continue to Payment
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>

                  <p className="text-xs text-center text-gray-500">
                    A secure popup window will open for payment
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="p-3 bg-gray-50 border-t">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <Shield className="h-3 w-3" />
            <span>256-bit SSL encryption • Your data is secure</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmbeddedCheckoutModal;
